/*!
 * @license
 * SPDX-License-Identifier: LicenseRef-cloudEQ-Proprietary
 * Copyright (c) 2026-present cloudEQ Inc. All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL.
 *
 * This source code, including all associated files, documentation, and
 * compiled forms, is the exclusive property of cloudEQ Inc. and is
 * protected by copyright, trade secret, and other intellectual property laws.
 *
 * This software is licensed, not sold, solely for use by authorized
 * licensees under the terms of a written license agreement with
 * cloudEQ Inc. Unless expressly permitted by that agreement, you may not
 * copy, modify, merge, distribute, sublicense, sell, publish, reverse
 * engineer, decompile, disassemble, or create derivative works of this
 * software, in whole or in part, by any means.
 *
 * Access to this file does not grant any license or right to use it.
 * Unauthorized use, reproduction, or disclosure is strictly prohibited and
 * may result in civil and criminal penalties.
 *
 * Contact: legal@cloudeq.com
 */

import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { BellRing, ChevronDown, Share, Smartphone, Monitor } from 'lucide-react';
import { cn } from '@/lib/cn';
import type { Platform, PushState } from './push';
import type { Push } from './usePush';

/**
 * Everything a person needs to know about push, in one screen.
 *
 * **This is not a settings panel with a help link.** It is seven different
 * sentences, and which one a person sees is the entire value of the screen —
 * because in three of those seven states the browser will not let us ask, and
 * without a screen that says so those people are simply lost: they believe push
 * is broken and we believe they declined.
 *
 * The hardest one is **iOS in a Safari tab**. Apple does not deliver push to a
 * tab at all; a permission prompt there produces nothing whatsoever. So this
 * screen shows no button in that state — a control that silently fails is how
 * somebody concludes the whole feature is broken — and shows the Add to Home
 * Screen steps instead.
 *
 * **The words here are the feature.** Every automated check in this repo is
 * blind to wording (docs/react-migration/plan.md §11), so `tests/ui/push-view.test.js`
 * drives each state and reads the sentences back.
 */

/** The heading and the paragraph for each state. One sentence each; no state shares. */
const COPY: Record<PushState, { title: string; body: string }> = {
  on: {
    title: 'Notifications are on for this device',
    body: 'You will hear about tasks you are on even when Qollab is closed. This is per device — turning it on here does not turn it on for your phone.',
  },
  off: {
    title: 'Get notified when Qollab is closed',
    body: 'A notification when you are assigned something, when somebody comments, or when work you are waiting on is finished. You will be asked to allow it once.',
  },
  denied: {
    title: 'Notifications are blocked for this site',
    body: 'You chose Block at some point, and a browser will not let us ask again — only you can undo it, in your browser settings for this site. Email keeps arriving either way.',
  },
  'ios-tab': {
    title: 'iPhone and iPad need one extra step',
    body: 'Apple only sends notifications to apps added to the Home Screen — never to a Safari tab. It takes about twenty seconds; the steps are below.',
  },
  'ios-old': {
    title: 'This version of iOS cannot do it',
    body: 'Apple added web notifications in iOS 16.4. Updating iOS will make this available; until then, email notifications carry on as they are.',
  },
  unsupported: {
    title: 'Not available in this browser',
    body: 'This browser does not support web notifications, or they are not switched on for this installation. Email notifications are unaffected.',
  },
};

/** How to undo a block, which is different in each browser and worth naming. */
const UNBLOCK = [
  'Chrome and Edge — tap the icon to the left of the address bar, then Notifications, then Allow',
  'Safari on Mac — Safari menu, Settings, Websites, Notifications, then Allow for this site',
  'iPhone and iPad — Settings, Notifications, Qollab',
];

export function PushDialog({
  open,
  onOpenChange,
  push,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  push: Push;
}) {
  const copy = COPY[push.state];
  const canTurnOn = push.state === 'off';

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay id="push-overlay" className="motion-backdrop open fixed inset-0 z-[70] bg-black/40" />
        <Dialog.Content
          id="push-modal"
          data-push-state={push.state}
          className="motion-sheet fixed left-1/2 top-1/2 z-[71] flex max-h-[88vh] w-[min(560px,94vw)] -translate-x-1/2 -translate-y-1/2 flex-col rounded-2xl bg-surface shadow-overlay"
        >
          <div className="modal-head">
            <div className="flex min-w-0 items-start gap-3">
              {/* A tinted disc rather than a bare glyph. The account menu's rows
                  are 15px icons in `ink-faint`; a dialog heading is the one place
                  in this app where the mark is allowed to carry colour, and
                  without it the screen opens looking like an error. */}
              <span className="mt-0.5 grid size-9 shrink-0 place-items-center rounded-xl bg-action-bg text-action">
                <BellRing className="size-[18px]" aria-hidden />
              </span>
              <div className="min-w-0">
                <Dialog.Title asChild>
                  <h2 id="push-title">{copy.title}</h2>
                </Dialog.Title>
                <Dialog.Description id="push-body" className="modal-creator">
                  {copy.body}
                </Dialog.Description>
              </div>
            </div>
            <Dialog.Close className="modal-close" aria-label="Close">
              ×
            </Dialog.Close>
          </div>

          {/* `pb-6` because this dialog has no footer — `.modal-body` leaves 6px at the
              bottom on the assumption one follows it, and without that the last
              line sits on the rounded corner. */}
          <div className="modal-body pb-6">
            {/* **The control, where it can do something.** `off` is the only
                state with a Turn on button: in `ios-tab` the prompt does
                nothing, in `denied` the browser refuses, and in the other three
                there is nothing to prompt for. A button in any of them would be
                a control that silently fails — see the note at the top. */}
            {canTurnOn && (
              <button
                id="push-enable"
                type="button"
                className="btn btn-primary w-full"
                disabled={push.busy}
                onClick={() => void push.enable()}
              >
                {push.busy ? 'Waiting for your answer…' : 'Turn on notifications'}
              </button>
            )}

            {push.state === 'on' && (
              <button
                id="push-disable"
                type="button"
                className="btn w-full"
                disabled={push.busy}
                onClick={() => void push.disable()}
              >
                {push.busy ? 'Turning off…' : 'Turn off on this device'}
              </button>
            )}

            {push.state === 'denied' && (
              <div id="push-unblock" className="rounded-xl border border-border bg-bg px-3 py-2.5">
                <p className="m-0 mb-1.5 text-2xs font-semibold uppercase tracking-[0.05em] text-ink-faint">
                  Where to change it
                </p>
                <ul className="m-0 flex list-none flex-col gap-1 p-0 text-xs text-ink-muted">
                  {UNBLOCK.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* **Both sets of instructions, always — with the one that applies
                open.** People hand each other screenshots, and a Windows user
                explaining this to somebody on an iPhone needs to be able to
                reach the iPhone steps. Collapsed, not hidden. */}
            <Instructions platform={push.platform} state={push.state} />

            {/* Said last, and said plainly. Push is an addition; somebody who
                cannot or will not turn it on has lost nothing. */}
            <p className="m-0 mt-3 text-2xs text-ink-faint">
              Email notifications are unchanged either way — this only adds a second
              way to hear, it never replaces the first.
            </p>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

const IOS_STEPS = [
  'Open qollab.ceqhub.com in Safari — it has to be Safari; Chrome on iPhone cannot do this',
  'Tap Share at the bottom of the screen — the box with an arrow pointing up',
  'Scroll down the list, tap Add to Home Screen, then tap Add',
  'From now on, open Qollab from that Home Screen icon rather than from Safari',
  'Open this screen again and tap Turn on notifications, then choose Allow',
];

const OTHER_STEPS = [
  'Nothing to install',
  'Tap Turn on notifications above, then choose Allow when the browser asks',
];

function Instructions({ platform, state }: { platform: Platform; state: PushState }) {
  // Which one starts open. The platform decides it, except in `ios-tab` where
  // the whole screen exists to deliver the iPhone steps.
  const [openPanel, setOpenPanel] = useState<'ios' | 'other'>(
    platform === 'ios' || state === 'ios-tab' ? 'ios' : 'other',
  );

  return (
    <div className="mt-3 flex flex-col gap-2">
      <Panel
        id="push-steps-ios"
        icon={<Smartphone className="size-4" aria-hidden />}
        label="iPhone and iPad"
        steps={IOS_STEPS}
        open={openPanel === 'ios'}
        onToggle={() => setOpenPanel('ios')}
      />
      <Panel
        id="push-steps-other"
        icon={<Monitor className="size-4" aria-hidden />}
        label="Android and desktop"
        steps={OTHER_STEPS}
        open={openPanel === 'other'}
        onToggle={() => setOpenPanel('other')}
      />
    </div>
  );
}

function Panel({
  id,
  icon,
  label,
  steps,
  open,
  onToggle,
}: {
  id: string;
  icon: React.ReactNode;
  label: string;
  steps: string[];
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <div className={cn('overflow-hidden rounded-xl border transition-colors duration-fast ease-out-soft', open ? 'border-border-strong bg-bg' : 'border-border')}>
      <button
        id={id}
        type="button"
        aria-expanded={open}
        onClick={onToggle}
        className="tap flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm font-semibold text-ink"
      >
        <span className="text-ink-faint">{icon}</span>
        {label}
        <ChevronDown
          className={cn('ml-auto size-4 shrink-0 text-ink-faint transition-transform duration-base ease-out-soft', open && 'rotate-180')}
          aria-hidden
        />
      </button>
      {open && (
        // A numbered list, because these are steps in an order and the third one
        // is where people stop reading. `<ol>` also means a screen reader says
        // "3 of 5" rather than reading five sentences with no shape.
        <ol className="m-0 flex list-decimal flex-col gap-1.5 py-1 pl-8 pr-3 pb-3 text-xs leading-relaxed text-ink-muted marker:font-semibold marker:text-ink-faint">
          {steps.map((s) => (
            <li key={s}>
              {/* The Share step is the one people cannot find, and naming the
                  icon in words is what makes it findable. */}
              {s.startsWith('Tap Share') && <Share className="mr-1 inline size-3.5 align-[-2px]" aria-hidden />}
              {s}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
